import { getProductThemeFromType } from "@components/common/Header/utils";
import { IconState } from "@components/molecules/IconUtils";
import { ProductCategory } from "@utils/constants";
import { useCallback, useRef, useState } from "react";
import { Dimensions, Platform } from "react-native";

const SCREEN_HEIGHT = Dimensions.get("window").height;

type GluestackMenuPlacement =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top left"
  | "top right"
  | "bottom left"
  | "bottom right"
  | "right top"
  | "right bottom"
  | "left top"
  | "left bottom";

const useThreeDotMenuState = (initialSelectedState = false) => {
  const [placement, setPlacement] = useState<GluestackMenuPlacement>("top");

  const [threeDotIconState, setThreeDotIconState] = useState(
    initialSelectedState ? IconState.Selected : IconState.Unselected
  );
  const [threeDotMenuOpen, setThreeDotMenuOpen] = useState(false);

  const threeDotMenuOpenRef = useRef(false);

  const setThreeDotMenuOpenTracked = useCallback((value: boolean) => {
    threeDotMenuOpenRef.current = value;
    setThreeDotMenuOpen(value);
  }, []);

  const handlePressIn = useCallback((event: any) => {
    setThreeDotIconState(IconState.Pressed);

    if (Platform.OS === "android") {
      const touchY = event.nativeEvent.pageY;
      if (touchY > SCREEN_HEIGHT - 300) {
        setPlacement("top");
      } else {
        setPlacement("bottom");
      }
    }
  }, []);

  const handlePressOut = useCallback(() => {
    if (!threeDotMenuOpenRef.current) {
      setThreeDotIconState(IconState.Unselected);
    } else {
      setThreeDotIconState((prevState) =>
        prevState === IconState.Pressed ? IconState.Selected : prevState
      );
    }
  }, []);

  const handlePress = useCallback(() => {
    const nextOpen = !threeDotMenuOpenRef.current;
    setThreeDotMenuOpenTracked(nextOpen);
    setThreeDotIconState(nextOpen ? IconState.Selected : IconState.Unselected);
  }, [setThreeDotMenuOpenTracked]);

  const handleClose = useCallback(() => {
    setThreeDotMenuOpenTracked(false);
    setThreeDotIconState(IconState.Unselected);
  }, [setThreeDotMenuOpenTracked]);

  const getThreeDotIconBackgroundColor = useCallback(
    (
      theme: any,
      productType: ProductCategory = ProductCategory.DEFAULT,
      isFavoritesIcon?: boolean,
      isDisabled?: boolean
    ) => {
      if (isDisabled) {
        return theme["core-02"];
      }
      if (threeDotMenuOpen) {
        return theme["core-05"];
      }
      const productTheme = getProductThemeFromType(productType);
      switch (threeDotIconState) {
        case IconState.Selected:
          return theme["core-05"];
        case IconState.Pressed:
          return theme["core-05"];
        default:
          return (theme.isLargeScreen && isFavoritesIcon) ||
            productType === ProductCategory.DEFAULT
            ? theme["core-06"]
            : theme[`${productTheme}-03`];
      }
    },
    [threeDotMenuOpen, threeDotIconState]
  );

  const getThreeDotIconColor = useCallback(
    (theme: any, productType: ProductCategory) => {
      if (threeDotMenuOpen) {
        return theme["core-01"];
      }
      const productTheme = getProductThemeFromType(productType);
      switch (threeDotIconState) {
        case IconState.Selected:
        case IconState.Pressed:
          return theme["core-01"];
        default:
          return theme[`${productTheme}-06`];
      }
    },
    [threeDotMenuOpen, threeDotIconState]
  );

  return {
    threeDotIconState,
    setThreeDotIconState,
    threeDotMenuOpen,
    setThreeDotMenuOpen: setThreeDotMenuOpenTracked,
    handlePressIn,
    handlePressOut,
    handlePress,
    handleClose,
    getThreeDotIconBackgroundColor,
    getThreeDotIconColor,
    placement,
  };
};

export default useThreeDotMenuState;
